import { Icon, type IconName } from '@/components/ui/Icon';
import { cn } from '@/utils/cn';

import { usePreferences } from '../../hooks/usePreferences';
import type { TextSize, Theme } from '../../preferences/preferences-store';
import styles from './AppearanceSettings.module.css';

const THEME_OPTIONS: { value: Theme; label: string; hint: string; icon: IconName }[] = [
  { value: 'light', label: 'Light', hint: 'Bright, for daytime', icon: 'sun' },
  { value: 'dark', label: 'Dark', hint: 'Easy on the eyes at night', icon: 'moon' },
  { value: 'system', label: 'Match device', hint: 'Follows this device', icon: 'monitor' },
];

const TEXT_SIZE_OPTIONS: { value: TextSize; label: string; hint: string }[] = [
  { value: 'standard', label: 'Standard', hint: 'The usual size' },
  { value: 'large', label: 'Large', hint: 'A step bigger' },
  { value: 'x-large', label: 'Extra large', hint: 'Easiest to read' },
];

const PREVIEW_CLASS: Record<Theme, string | undefined> = {
  light: styles.previewLight,
  dark: styles.previewDark,
  system: styles.previewSystem,
};

const SAMPLE_CLASS: Record<TextSize, string | undefined> = {
  standard: styles.sampleStandard,
  large: styles.sampleLarge,
  'x-large': styles.sampleXLarge,
};

/** The theme picker. The choice applies at once and is remembered on this device. */
export function ThemeSettings() {
  const { theme, setTheme } = usePreferences();

  return (
    <fieldset className={styles.fieldset}>
      <legend className={styles.hiddenLegend}>Theme</legend>
      <div className={styles.options}>
        {THEME_OPTIONS.map((option) => (
          <label
            key={option.value}
            className={cn(styles.option, theme === option.value && styles.selected)}
          >
            <input
              type="radio"
              name="theme"
              value={option.value}
              checked={theme === option.value}
              onChange={() => {
                setTheme(option.value);
              }}
              className={styles.radio}
            />
            <span className={cn(styles.preview, PREVIEW_CLASS[option.value])}>
              <Icon name={option.icon} size={22} />
            </span>
            <span className={styles.optionLabel}>{option.label}</span>
            <span className={styles.optionHint}>{option.hint}</span>
            <Icon name="check" size={18} className={styles.tick} />
          </label>
        ))}
      </div>
    </fieldset>
  );
}

/** The text size picker. The choice applies at once and is remembered on this device. */
export function TextSizeSettings() {
  const { textSize, setTextSize } = usePreferences();

  return (
    <fieldset className={styles.fieldset}>
      <legend className={styles.hiddenLegend}>Text size</legend>
      <div className={styles.options}>
        {TEXT_SIZE_OPTIONS.map((option) => (
          <label
            key={option.value}
            className={cn(styles.option, textSize === option.value && styles.selected)}
          >
            <input
              type="radio"
              name="text-size"
              value={option.value}
              checked={textSize === option.value}
              onChange={() => {
                setTextSize(option.value);
              }}
              className={styles.radio}
            />
            <span className={cn(styles.sample, SAMPLE_CLASS[option.value])} aria-hidden="true">
              Aa
            </span>
            <span className={styles.optionLabel}>{option.label}</span>
            <span className={styles.optionHint}>{option.hint}</span>
            <Icon name="check" size={18} className={styles.tick} />
          </label>
        ))}
      </div>
    </fieldset>
  );
}
